import jsPDF from 'jspdf';
import 'jspdf-autotable';

const APP_NAME = 'Calm Pathways';
const PRIMARY_COLOR = [124, 152, 133]; // #7C9885
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format dates (treats YYYY-MM-DD as local to avoid UTC off-by-one)
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? new Date(`${dateStr}T00:00:00`) : new Date(dateStr);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Fetch attachment metadata + binary for a record. Returns array of {id, original_filename, dataUrl, width, height}.
// Token can be passed explicitly or read from localStorage.
const fetchAttachmentsForPdf = async (parentType, parentId, token) => {
  const authToken = token || localStorage.getItem('token');
  if (!authToken || !API_URL || !parentId) return [];
  try {
    const listRes = await fetch(
      `${API_URL}/api/attachments?parent_type=${parentType}&parent_id=${encodeURIComponent(parentId)}`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    if (!listRes.ok) return [];
    const { attachments = [] } = await listRes.json();
    if (attachments.length === 0) return [];

    const results = [];
    for (const att of attachments) {
      try {
        const fileRes = await fetch(
          `${API_URL}/api/attachments/${att.id}/download?auth=${encodeURIComponent(authToken)}`
        );
        if (!fileRes.ok) continue;
        const blob = await fileRes.blob();
        const dataUrl = await blobToDataUrl(blob);
        const dims = await imageDimensions(dataUrl);
        results.push({
          id: att.id,
          original_filename: att.original_filename,
          content_type: att.content_type,
          dataUrl,
          width: dims.width,
          height: dims.height,
        });
      } catch (e) {
        // Skip any that fail
      }
    }
    return results;
  } catch (e) {
    return [];
  }
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const imageDimensions = (dataUrl) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
  img.onerror = () => resolve({ width: 0, height: 0 });
  img.src = dataUrl;
});

// Append attachment thumbnails to the PDF starting at yPos. Returns new yPos.
// Each photo: caption above, scaled to fit page width up to maxHeight.
const appendAttachmentsToPdf = (doc, attachments, startY, title = 'Attached Photos & Documents') => {
  if (!attachments || attachments.length === 0) return startY;
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 14;
  const maxImgWidth = pageWidth - margin * 2;
  const maxImgHeight = 110; // mm

  let yPos = startY;
  // Section heading
  if (yPos > pageHeight - 60) { doc.addPage(); yPos = 20; }
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(title, margin, yPos);
  yPos += 6;

  for (const att of attachments) {
    // Compute scaled dimensions preserving aspect ratio
    const ratio = att.width && att.height ? att.width / att.height : 1;
    let drawW = maxImgWidth;
    let drawH = drawW / ratio;
    if (drawH > maxImgHeight) {
      drawH = maxImgHeight;
      drawW = drawH * ratio;
    }

    // Need room for caption (5mm) + image + spacing (5mm)
    const blockHeight = drawH + 12;
    if (yPos + blockHeight > pageHeight - 20) {
      doc.addPage();
      yPos = 20;
    }

    // Caption
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    const captionText = att.original_filename || 'Attachment';
    doc.text(captionText, margin, yPos);
    yPos += 4;

    // Image — jsPDF picks format from data URL; force JPEG/PNG based on content_type
    const format =
      (att.content_type && att.content_type.toLowerCase().includes('png')) ? 'PNG' : 'JPEG';
    try {
      doc.addImage(att.dataUrl, format, margin, yPos, drawW, drawH, undefined, 'FAST');
    } catch (e) {
      // If format auto-detect fails (e.g. webp), try the alternative
      try {
        const alt = format === 'PNG' ? 'JPEG' : 'PNG';
        doc.addImage(att.dataUrl, alt, margin, yPos, drawW, drawH, undefined, 'FAST');
      } catch (e2) {
        // skip silently
      }
    }
    yPos += drawH + 6;
    doc.setTextColor(0, 0, 0);
  }
  return yPos;
};

// Add header to PDF
const addHeader = (doc, title, subtitle) => {
  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(0, 0, doc.internal.pageSize.width, 35, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(APP_NAME, 14, 18);
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(title, 14, 28);
  
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.text(`Generated: ${formatDate(new Date().toISOString())}`, 14, 45);
  
  if (subtitle) {
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(subtitle, 14, 52);
  }
  
  return subtitle ? 60 : 55;
};

// Add footer to each page
const addFooter = (doc) => {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount} | ${APP_NAME} - For informational purposes only. Consult your healthcare provider.`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }
};

// Export Lab Results to PDF
export const exportLabResultsToPDF = (labResults, categories) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Lab Results Report', 'Track and share your lab values with healthcare providers');
  
  if (labResults.length === 0) {
    doc.text('No lab results to display.', 14, yPos);
    addFooter(doc);
    doc.save('lab-results.pdf');
    return;
  }

  // Group results by date
  const groupedByDate = labResults.reduce((acc, result) => {
    const date = result.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(result);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedByDate).sort((a, b) => new Date(b) - new Date(a));

  sortedDates.forEach((date, dateIndex) => {
    if (dateIndex > 0) {
      yPos += 10;
    }
    
    // Check if we need a new page
    if (yPos > doc.internal.pageSize.height - 50) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text(formatDate(date), 14, yPos);
    yPos += 5;

    const tableData = groupedByDate[date].map(result => [
      categories[result.category]?.name || result.category,
      result.test_name,
      `${result.value} ${result.unit || ''}`.trim(),
      result.reference_range || '-',
      result.lab_name || '-'
    ]);

    doc.autoTable({
      startY: yPos,
      head: [['Category', 'Test', 'Result', 'Reference', 'Lab']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: PRIMARY_COLOR, fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 45 },
        2: { cellWidth: 30 },
        3: { cellWidth: 35 },
        4: { cellWidth: 35 }
      },
      margin: { left: 14, right: 14 }
    });

    yPos = doc.lastAutoTable.finalY + 5;
  });

  addFooter(doc);
  doc.save('lab-results.pdf');
};

// Export Symptom Entries to PDF
export const exportSymptomsToPDF = (entries) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Symptom Tracking Report', 'Document symptoms, triggers, and severity over time');
  
  if (entries.length === 0) {
    doc.text('No symptom entries to display.', 14, yPos);
    addFooter(doc);
    doc.save('symptom-tracker.pdf');
    return;
  }

  // Sort entries by date (newest first)
  const sortedEntries = [...entries].sort((a, b) => new Date(b.date) - new Date(a.date));

  sortedEntries.forEach((entry, index) => {
    if (yPos > doc.internal.pageSize.height - 60) {
      doc.addPage();
      yPos = 20;
    }

    // Date header
    doc.setFillColor(245, 245, 244);
    doc.rect(14, yPos - 5, doc.internal.pageSize.width - 28, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text(`${formatDate(entry.date)} - ${entry.child_name || 'Child'}`, 16, yPos);
    yPos += 8;

    // Severity
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(`Severity: ${entry.severity}/10`, 16, yPos);
    yPos += 6;

    // Symptoms
    if (entry.symptoms && entry.symptoms.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text('Symptoms:', 16, yPos);
      doc.setFont('helvetica', 'normal');
      const symptomsText = entry.symptoms.join(', ');
      const splitSymptoms = doc.splitTextToSize(symptomsText, 170);
      doc.text(splitSymptoms, 50, yPos);
      yPos += splitSymptoms.length * 4 + 4;
    }

    // Triggers
    if (entry.triggers && entry.triggers.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text('Triggers:', 16, yPos);
      doc.setFont('helvetica', 'normal');
      const triggersText = entry.triggers.join(', ');
      const splitTriggers = doc.splitTextToSize(triggersText, 170);
      doc.text(splitTriggers, 50, yPos);
      yPos += splitTriggers.length * 4 + 4;
    }

    // Notes
    if (entry.notes) {
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 16, yPos);
      doc.setFont('helvetica', 'normal');
      const splitNotes = doc.splitTextToSize(entry.notes, 170);
      doc.text(splitNotes, 50, yPos);
      yPos += splitNotes.length * 4 + 4;
    }

    yPos += 8;
  });

  addFooter(doc);
  doc.save('symptom-tracker.pdf');
};

// Export Food Logs to PDF
export const exportFoodLogsToPDF = (foodLogs) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Food Tracking Report', 'Document meals and reactions to identify patterns');
  
  if (foodLogs.length === 0) {
    doc.text('No food logs to display.', 14, yPos);
    addFooter(doc);
    doc.save('food-tracker.pdf');
    return;
  }

  // Group by date
  const groupedByDate = foodLogs.reduce((acc, log) => {
    const date = log.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedByDate).sort((a, b) => new Date(b) - new Date(a));

  sortedDates.forEach((date) => {
    if (yPos > doc.internal.pageSize.height - 50) {
      doc.addPage();
      yPos = 20;
    }

    // Date header
    doc.setFillColor(245, 245, 244);
    doc.rect(14, yPos - 5, doc.internal.pageSize.width - 28, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text(formatDate(date), 16, yPos);
    yPos += 10;

    const dayLogs = groupedByDate[date].sort((a, b) => {
      const order = { breakfast: 1, lunch: 2, dinner: 3, snack: 4 };
      return (order[a.meal_type] || 5) - (order[b.meal_type] || 5);
    });

    dayLogs.forEach((log) => {
      if (yPos > doc.internal.pageSize.height - 40) {
        doc.addPage();
        yPos = 20;
      }

      // Meal type
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      const mealLabel = log.meal_type.charAt(0).toUpperCase() + log.meal_type.slice(1);
      doc.text(`${mealLabel}:`, 18, yPos);
      yPos += 5;

      // Foods
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      if (log.foods && log.foods.length > 0) {
        const foodsText = log.foods.join(', ');
        const splitFoods = doc.splitTextToSize(foodsText, 165);
        doc.text(splitFoods, 22, yPos);
        yPos += splitFoods.length * 4 + 2;
      }

      // Reactions
      if (log.reactions && log.reactions.length > 0) {
        doc.setTextColor(180, 80, 80);
        doc.setFont('helvetica', 'bold');
        doc.text('Reactions: ', 22, yPos);
        doc.setFont('helvetica', 'normal');
        const reactionsText = log.reactions.join(', ');
        doc.text(reactionsText, 48, yPos);
        yPos += 5;
        doc.setTextColor(0, 0, 0);
      }

      // Notes
      if (log.notes) {
        doc.setTextColor(100, 100, 100);
        const splitNotes = doc.splitTextToSize(`Note: ${log.notes}`, 165);
        doc.text(splitNotes, 22, yPos);
        yPos += splitNotes.length * 4 + 2;
        doc.setTextColor(0, 0, 0);
      }

      yPos += 4;
    });

    yPos += 6;
  });

  addFooter(doc);
  doc.save('food-tracker.pdf');
};

// Export Supplements & Medications to PDF
export const exportSupplementsToPDF = (supplements, reactions) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Supplement & Medication Report', 'Complete list of supplements, medications, and reactions');
  
  if (supplements.length === 0) {
    doc.text('No supplements or medications to display.', 14, yPos);
    addFooter(doc);
    doc.save('supplements-medications.pdf');
    return;
  }

  // Active Supplements Section
  const activeSupplements = supplements.filter(s => s.type === 'supplement' && s.status === 'active');
  if (activeSupplements.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text('Active Supplements', 14, yPos);
    yPos += 5;

    const supplementData = activeSupplements.map(s => [
      s.name,
      s.dosage,
      s.frequency,
      s.time_of_day || '-',
      s.brand || '-',
      formatDate(s.start_date)
    ]);

    doc.autoTable({
      startY: yPos,
      head: [['Name', 'Dosage', 'Frequency', 'Time', 'Brand', 'Started']],
      body: supplementData,
      theme: 'striped',
      headStyles: { fillColor: PRIMARY_COLOR, fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 }
    });

    yPos = doc.lastAutoTable.finalY + 10;
  }

  // Active Medications Section
  const activeMedications = supplements.filter(s => s.type === 'medication' && s.status === 'active');
  if (activeMedications.length > 0) {
    if (yPos > doc.internal.pageSize.height - 60) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text('Active Medications', 14, yPos);
    yPos += 5;

    const medData = activeMedications.map(s => [
      s.name,
      s.dosage,
      s.frequency,
      s.prescriber || '-',
      s.reason || '-',
      formatDate(s.start_date)
    ]);

    doc.autoTable({
      startY: yPos,
      head: [['Name', 'Dosage', 'Frequency', 'Prescriber', 'Reason', 'Started']],
      body: medData,
      theme: 'striped',
      headStyles: { fillColor: [139, 92, 246], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 }
    });

    yPos = doc.lastAutoTable.finalY + 10;
  }

  // Paused/Discontinued Section
  const inactiveItems = supplements.filter(s => s.status !== 'active');
  if (inactiveItems.length > 0) {
    if (yPos > doc.internal.pageSize.height - 60) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(150, 150, 150);
    doc.text('Paused/Discontinued Items', 14, yPos);
    yPos += 5;

    const inactiveData = inactiveItems.map(s => [
      s.name,
      s.type === 'medication' ? 'Medication' : 'Supplement',
      s.dosage,
      s.status.charAt(0).toUpperCase() + s.status.slice(1),
      formatDate(s.start_date),
      s.end_date ? formatDate(s.end_date) : '-'
    ]);

    doc.autoTable({
      startY: yPos,
      head: [['Name', 'Type', 'Dosage', 'Status', 'Started', 'Ended']],
      body: inactiveData,
      theme: 'striped',
      headStyles: { fillColor: [150, 150, 150], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 }
    });

    yPos = doc.lastAutoTable.finalY + 10;
  }

  // Reactions Section
  if (reactions && reactions.length > 0) {
    if (yPos > doc.internal.pageSize.height - 60) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38);
    doc.text('Adverse Reactions & Intolerances', 14, yPos);
    yPos += 8;

    reactions.forEach((reaction) => {
      if (yPos > doc.internal.pageSize.height - 40) {
        doc.addPage();
        yPos = 20;
      }

      const supplement = supplements.find(s => s.id === reaction.supplement_id);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`${supplement?.name || 'Unknown'} - ${reaction.severity.toUpperCase()} ${reaction.reaction_type.replace('_', ' ')}`, 16, yPos);
      yPos += 5;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Date: ${formatDate(reaction.date_occurred)}`, 18, yPos);
      yPos += 4;

      if (reaction.symptoms && reaction.symptoms.length > 0) {
        doc.text(`Symptoms: ${reaction.symptoms.join(', ')}`, 18, yPos);
        yPos += 4;
      }

      if (reaction.notes) {
        const splitNotes = doc.splitTextToSize(`Notes: ${reaction.notes}`, 170);
        doc.text(splitNotes, 18, yPos);
        yPos += splitNotes.length * 4;
      }

      yPos += 6;
    });
  }

  addFooter(doc);
  doc.save('supplements-medications.pdf');
};

// Export all appointments to PDF
export const exportAppointmentsToPDF = async (appointments, token = null) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Appointment History', 'Complete record of provider visits and notes');
  
  if (appointments.length === 0) {
    doc.text('No appointments to display.', 14, yPos);
    addFooter(doc);
    doc.save('appointments.pdf');
    return;
  }

  const sorted = [...appointments].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Bulk-fetch all attachments at once
  let attachmentsByAppt = {};
  try {
    const authToken = token || localStorage.getItem('token');
    if (authToken && API_URL) {
      const ids = sorted.map(a => a.id).join(',');
      const res = await fetch(
        `${API_URL}/api/attachments/by-records?parent_type=appointment&parent_ids=${encodeURIComponent(ids)}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.ok) {
        const { attachments = [] } = await res.json();
        attachmentsByAppt = attachments.reduce((acc, a) => {
          (acc[a.parent_id] = acc[a.parent_id] || []).push(a);
          return acc;
        }, {});
      }
    }
  } catch (e) { /* noop */ }

  for (const appt of sorted) {
    if (yPos > doc.internal.pageSize.height - 80) {
      doc.addPage();
      yPos = 20;
    }

    // Date and status header
    doc.setFillColor(245, 245, 244);
    doc.rect(14, yPos - 5, doc.internal.pageSize.width - 28, 10, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text(`${formatDate(appt.date)}${appt.time ? ' at ' + appt.time : ''} - ${appt.status.toUpperCase()}`, 16, yPos);
    yPos += 12;

    // Provider info
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(appt.provider_name, 16, yPos);
    yPos += 5;
    
    if (appt.provider_title) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(appt.provider_title, 16, yPos);
      yPos += 4;
    }
    
    if (appt.company_name) {
      doc.setFontSize(9);
      doc.text(appt.company_name, 16, yPos);
      yPos += 4;
    }

    // Contact info
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    if (appt.phone) { doc.text(`Phone: ${appt.phone}`, 16, yPos); yPos += 3; }
    if (appt.address) { doc.text(`Address: ${appt.address}`, 16, yPos); yPos += 3; }
    yPos += 2;

    // Summary
    if (appt.summary) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 100, 0);
      doc.text('Summary:', 16, yPos);
      yPos += 4;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const splitSummary = doc.splitTextToSize(appt.summary, 170);
      doc.text(splitSummary, 18, yPos);
      yPos += splitSummary.length * 4 + 2;
    }

    // Medications
    if (appt.meds_ordered?.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 150);
      doc.text('Meds Ordered: ', 16, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(appt.meds_ordered.join(', '), 50, yPos);
      yPos += 4;
    }
    if (appt.meds_changed?.length > 0) {
      doc.setTextColor(150, 100, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('Meds Changed: ', 16, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(appt.meds_changed.join(', '), 50, yPos);
      yPos += 4;
    }
    if (appt.meds_discontinued?.length > 0) {
      doc.setTextColor(150, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('Meds Stopped: ', 16, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(appt.meds_discontinued.join(', '), 50, yPos);
      yPos += 4;
    }

    // Labs
    doc.setTextColor(0, 0, 0);
    if (appt.labs_ordered?.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text('Labs Ordered: ', 16, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(appt.labs_ordered.join(', '), 50, yPos);
      yPos += 4;
    }

    // Follow-up
    if (appt.follow_up_instructions) {
      doc.setFont('helvetica', 'bold');
      doc.text('Follow-up: ', 16, yPos);
      doc.setFont('helvetica', 'normal');
      const splitFollowup = doc.splitTextToSize(appt.follow_up_instructions, 150);
      doc.text(splitFollowup, 45, yPos);
      yPos += splitFollowup.length * 4;
    }

    // Attached photos for this appointment
    const apptAtts = attachmentsByAppt[appt.id];
    if (apptAtts && apptAtts.length > 0) {
      const authToken = token || localStorage.getItem('token');
      const loaded = [];
      for (const a of apptAtts) {
        try {
          const fr = await fetch(`${API_URL}/api/attachments/${a.id}/download?auth=${encodeURIComponent(authToken)}`);
          if (!fr.ok) continue;
          const blob = await fr.blob();
          const dataUrl = await blobToDataUrl(blob);
          const dims = await imageDimensions(dataUrl);
          loaded.push({ ...a, dataUrl, width: dims.width, height: dims.height });
        } catch (e) { /* skip */ }
      }
      if (loaded.length > 0) {
        yPos = appendAttachmentsToPdf(doc, loaded, yPos + 4, `Attached Photos (${apptAtts.length})`);
      }
    }

    yPos += 10;
  }

  addFooter(doc);
  doc.save('appointments.pdf');
};

// Export single appointment to PDF
export const exportSingleAppointmentPDF = async (appt, token = null) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Appointment Summary', `${appt.provider_name} - ${formatDate(appt.date)}`);

  // Provider details
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(appt.provider_name, 14, yPos);
  yPos += 6;

  if (appt.provider_title) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(appt.provider_title, 14, yPos);
    yPos += 5;
  }

  if (appt.company_name) {
    doc.setFontSize(10);
    doc.text(appt.company_name, 14, yPos);
    yPos += 5;
  }

  // Contact box
  if (appt.phone || appt.email || appt.address) {
    doc.setFillColor(245, 245, 244);
    doc.rect(14, yPos, doc.internal.pageSize.width - 28, 20, 'F');
    yPos += 5;
    doc.setFontSize(9);
    if (appt.phone) { doc.text(`Phone: ${appt.phone}`, 18, yPos); yPos += 4; }
    if (appt.email) { doc.text(`Email: ${appt.email}`, 18, yPos); yPos += 4; }
    if (appt.address) { doc.text(`Address: ${appt.address}`, 18, yPos); yPos += 4; }
    yPos += 5;
  }

  yPos += 5;

  // Date/Time/Status
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text(`Date: ${formatDate(appt.date)}${appt.time ? ' at ' + appt.time : ''}`, 14, yPos);
  yPos += 5;
  doc.text(`Status: ${appt.status.toUpperCase()}`, 14, yPos);
  if (appt.appointment_type) {
    doc.text(`Type: ${appt.appointment_type}`, 80, yPos);
  }
  yPos += 10;

  // Questions for provider
  if (appt.questions_for_provider?.length > 0) {
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Questions for Provider:', 14, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    appt.questions_for_provider.forEach((q, i) => {
      doc.text(`${i + 1}. ${q}`, 18, yPos);
      yPos += 5;
    });
    yPos += 5;
  }

  // Concerns
  if (appt.concerns_to_discuss) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Concerns:', 14, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const splitConcerns = doc.splitTextToSize(appt.concerns_to_discuss, 175);
    doc.text(splitConcerns, 14, yPos);
    yPos += splitConcerns.length * 4 + 5;
  }

  // Summary
  if (appt.summary) {
    doc.setFillColor(220, 252, 231);
    doc.rect(14, yPos - 3, doc.internal.pageSize.width - 28, 25, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 100, 0);
    doc.text('Visit Summary:', 16, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    const splitSummary = doc.splitTextToSize(appt.summary, 170);
    doc.text(splitSummary, 16, yPos);
    yPos += Math.max(splitSummary.length * 4 + 8, 20);
  }

  // Medications section
  if (appt.meds_ordered?.length > 0 || appt.meds_changed?.length > 0 || appt.meds_discontinued?.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Medication Changes:', 14, yPos);
    yPos += 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    if (appt.meds_ordered?.length > 0) {
      doc.setTextColor(0, 0, 150);
      doc.text(`• Ordered: ${appt.meds_ordered.join(', ')}`, 16, yPos);
      yPos += 5;
    }
    if (appt.meds_changed?.length > 0) {
      doc.setTextColor(150, 100, 0);
      doc.text(`• Changed: ${appt.meds_changed.join(', ')}`, 16, yPos);
      yPos += 5;
    }
    if (appt.meds_discontinued?.length > 0) {
      doc.setTextColor(150, 0, 0);
      doc.text(`• Discontinued: ${appt.meds_discontinued.join(', ')}`, 16, yPos);
      yPos += 5;
    }
    yPos += 5;
  }

  // Labs
  doc.setTextColor(0, 0, 0);
  if (appt.labs_ordered?.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Labs Ordered:', 14, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(appt.labs_ordered.join(', '), 14, yPos);
    yPos += 8;
  }

  if (appt.labs_reviewed) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Labs Reviewed:', 14, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const splitLabs = doc.splitTextToSize(appt.labs_reviewed, 175);
    doc.text(splitLabs, 14, yPos);
    yPos += splitLabs.length * 4 + 5;
  }

  // Follow-up
  if (appt.follow_up_instructions) {
    doc.setFillColor(254, 243, 199);
    doc.rect(14, yPos - 3, doc.internal.pageSize.width - 28, 20, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(150, 100, 0);
    doc.text('Follow-up Instructions:', 16, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    const splitFollowup = doc.splitTextToSize(appt.follow_up_instructions, 170);
    doc.text(splitFollowup, 16, yPos);
    yPos += Math.max(splitFollowup.length * 4 + 8, 15);
  }

  // Notes
  if (appt.notes) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Additional Notes:', 14, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const splitNotes = doc.splitTextToSize(appt.notes, 175);
    doc.text(splitNotes, 14, yPos);
    yPos += splitNotes.length * 4 + 8;
  }

  // Attached photos & documents
  try {
    const attachments = await fetchAttachmentsForPdf('appointment', appt.id, token);
    if (attachments.length > 0) {
      yPos = appendAttachmentsToPdf(doc, attachments, yPos + 4, 'Attached Photos & Documents');
    }
  } catch (e) {
    // Non-fatal; skip attachments
  }

  addFooter(doc);
  doc.save(`appointment-${appt.provider_name.replace(/\s+/g, '-')}-${appt.date}.pdf`);
};

// Export Care Team Summary PDF - combines providers, medications, and recent appointments
export const exportCareTeamSummaryPDF = async (providers, supplements, appointments, childName = 'My Child', token = null) => {
  const doc = new jsPDF();
  let yPos = 20;

  // Custom header for Care Team Summary
  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(0, 0, doc.internal.pageSize.width, 45, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Care Team Summary', 14, 20);
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text(`for ${childName}`, 14, 30);
  
  doc.setFontSize(10);
  doc.text(`Generated: ${formatDate(new Date().toISOString())}`, 14, 40);
  
  yPos = 55;

  // Important notice box
  doc.setFillColor(254, 243, 199);
  doc.rect(14, yPos, doc.internal.pageSize.width - 28, 15, 'F');
  doc.setFontSize(9);
  doc.setTextColor(150, 100, 0);
  doc.setFont('helvetica', 'bold');
  doc.text('Note: This document is for informational purposes. Please verify all information with your healthcare providers.', 18, yPos + 9);
  yPos += 25;

  // ========== SECTION 1: CARE TEAM (PROVIDERS) ==========
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('Care Team', 14, yPos);
  yPos += 3;
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.line(14, yPos, 80, yPos);
  yPos += 8;

  if (providers && providers.length > 0) {
    const providerData = providers.map(p => [
      p.name + (p.title ? `, ${p.title}` : ''),
      p.specialty || '-',
      p.company_name || '-',
      p.phone || '-'
    ]);

    doc.autoTable({
      startY: yPos,
      head: [['Provider', 'Specialty', 'Practice', 'Phone']],
      body: providerData,
      theme: 'striped',
      headStyles: { fillColor: PRIMARY_COLOR, fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 40 }, 2: { cellWidth: 50 }, 3: { cellWidth: 35 } },
      margin: { left: 14, right: 14 }
    });
    yPos = doc.lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('No providers saved yet.', 14, yPos);
    yPos += 10;
  }

  // ========== SECTION 2: CURRENT MEDICATIONS ==========
  if (yPos > doc.internal.pageSize.height - 80) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('Current Medications', 14, yPos);
  yPos += 3;
  doc.line(14, yPos, 100, yPos);
  yPos += 8;

  const activeMeds = supplements ? supplements.filter(s => s.type === 'medication' && s.status === 'active') : [];
  
  if (activeMeds.length > 0) {
    const medData = activeMeds.map(m => [
      m.name,
      m.dosage,
      m.frequency,
      m.prescriber || '-',
      formatDate(m.start_date)
    ]);

    doc.autoTable({
      startY: yPos,
      head: [['Medication', 'Dosage', 'Frequency', 'Prescriber', 'Started']],
      body: medData,
      theme: 'striped',
      headStyles: { fillColor: [139, 92, 246], fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 }
    });
    yPos = doc.lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('No active medications.', 14, yPos);
    yPos += 10;
  }

  // ========== SECTION 3: CURRENT SUPPLEMENTS ==========
  if (yPos > doc.internal.pageSize.height - 80) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('Current Supplements', 14, yPos);
  yPos += 3;
  doc.line(14, yPos, 105, yPos);
  yPos += 8;

  const activeSupps = supplements ? supplements.filter(s => s.type === 'supplement' && s.status === 'active') : [];
  
  if (activeSupps.length > 0) {
    const suppData = activeSupps.map(s => [
      s.name,
      s.dosage,
      s.frequency,
      s.brand || '-',
      s.reason || '-'
    ]);

    doc.autoTable({
      startY: yPos,
      head: [['Supplement', 'Dosage', 'Frequency', 'Brand', 'Reason']],
      body: suppData,
      theme: 'striped',
      headStyles: { fillColor: PRIMARY_COLOR, fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 }
    });
    yPos = doc.lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('No active supplements.', 14, yPos);
    yPos += 10;
  }

  // ========== SECTION 4: KNOWN REACTIONS/ALLERGIES ==========
  if (yPos > doc.internal.pageSize.height - 60) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(220, 38, 38);
  doc.text('⚠ Known Reactions & Intolerances', 14, yPos);
  yPos += 3;
  doc.setDrawColor(220, 38, 38);
  doc.line(14, yPos, 130, yPos);
  yPos += 8;

  const discontinuedItems = supplements ? supplements.filter(s => s.status === 'discontinued') : [];
  
  if (discontinuedItems.length > 0) {
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    discontinuedItems.forEach(item => {
      if (yPos > doc.internal.pageSize.height - 20) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFont('helvetica', 'bold');
      doc.text(`• ${item.name}`, 16, yPos);
      doc.setFont('helvetica', 'normal');
      if (item.notes) {
        doc.setTextColor(100, 100, 100);
        doc.text(` - ${item.notes}`, 16 + doc.getTextWidth(`• ${item.name}`), yPos);
        doc.setTextColor(0, 0, 0);
      }
      yPos += 5;
    });
    yPos += 5;
  } else {
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('No known reactions or discontinued items recorded.', 14, yPos);
    yPos += 10;
  }

  // ========== SECTION 5: RECENT APPOINTMENTS ==========
  if (yPos > doc.internal.pageSize.height - 80) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('Recent Appointments (Last 6 months)', 14, yPos);
  yPos += 3;
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.line(14, yPos, 135, yPos);
  yPos += 8;

  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  const recentAppts = appointments ? appointments
    .filter(a => new Date(a.date) >= sixMonthsAgo)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 10) : [];

  if (recentAppts.length > 0) {
    recentAppts.forEach(appt => {
      if (yPos > doc.internal.pageSize.height - 40) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFillColor(245, 245, 244);
      doc.rect(14, yPos - 3, doc.internal.pageSize.width - 28, 8, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...PRIMARY_COLOR);
      doc.text(`${formatDate(appt.date)} - ${appt.provider_name}`, 16, yPos + 2);
      if (appt.appointment_type) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(` (${appt.appointment_type})`, 16 + doc.getTextWidth(`${formatDate(appt.date)} - ${appt.provider_name}`), yPos + 2);
      }
      yPos += 10;

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      
      if (appt.summary) {
        doc.setFont('helvetica', 'normal');
        const splitSummary = doc.splitTextToSize(appt.summary, 170);
        doc.text(splitSummary, 18, yPos);
        yPos += splitSummary.length * 4 + 2;
      }

      if (appt.meds_ordered?.length > 0 || appt.meds_changed?.length > 0) {
        doc.setFont('helvetica', 'bold');
        let medChanges = [];
        if (appt.meds_ordered?.length > 0) medChanges.push(`Started: ${appt.meds_ordered.join(', ')}`);
        if (appt.meds_changed?.length > 0) medChanges.push(`Changed: ${appt.meds_changed.join(', ')}`);
        doc.text(`Medications: ${medChanges.join(' | ')}`, 18, yPos);
        yPos += 5;
      }

      yPos += 5;
    });
  } else {
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('No appointments in the last 6 months.', 14, yPos);
    yPos += 10;
  }

  // ========== SECTION 6: UPCOMING APPOINTMENTS ==========
  const today = new Date().toISOString().split('T')[0];
  const upcomingAppts = appointments ? appointments
    .filter(a => a.date >= today && a.status === 'scheduled')
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5) : [];

  if (upcomingAppts.length > 0) {
    if (yPos > doc.internal.pageSize.height - 60) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 100, 200);
    doc.text('Upcoming Appointments', 14, yPos);
    yPos += 3;
    doc.setDrawColor(0, 100, 200);
    doc.line(14, yPos, 105, yPos);
    yPos += 8;

    const upcomingData = upcomingAppts.map(a => [
      formatDate(a.date),
      a.time || '-',
      a.provider_name,
      a.appointment_type || '-'
    ]);

    doc.autoTable({
      startY: yPos,
      head: [['Date', 'Time', 'Provider', 'Type']],
      body: upcomingData,
      theme: 'striped',
      headStyles: { fillColor: [0, 100, 200], fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 }
    });
    yPos = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 10 : yPos + 30;
  }

  // ========== SECTION 7: APPOINTMENT PHOTOS ==========
  // Fetch attachments for the recent appointments and embed them as a final reference gallery.
  try {
    const authToken = token || localStorage.getItem('token');
    if (authToken && API_URL && recentAppts.length > 0) {
      const ids = recentAppts.map(a => a.id).join(',');
      const listRes = await fetch(
        `${API_URL}/api/attachments/by-records?parent_type=appointment&parent_ids=${encodeURIComponent(ids)}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (listRes.ok) {
        const { attachments: allAtts = [] } = await listRes.json();
        if (allAtts.length > 0) {
          // Page break
          doc.addPage();
          let photosY = addHeader(doc, 'Appointment Photos', `Visual references for ${childName}'s care team`);

          // Group attachments by appointment, preserving recentAppts ordering (newest first)
          const byAppt = allAtts.reduce((acc, a) => {
            (acc[a.parent_id] = acc[a.parent_id] || []).push(a);
            return acc;
          }, {});

          for (const appt of recentAppts) {
            const apptAtts = byAppt[appt.id];
            if (!apptAtts || apptAtts.length === 0) continue;

            // Load image data for this appointment
            const loaded = [];
            for (const a of apptAtts) {
              try {
                const fr = await fetch(`${API_URL}/api/attachments/${a.id}/download?auth=${encodeURIComponent(authToken)}`);
                if (!fr.ok) continue;
                const blob = await fr.blob();
                const dataUrl = await blobToDataUrl(blob);
                const dims = await imageDimensions(dataUrl);
                loaded.push({ ...a, dataUrl, width: dims.width, height: dims.height });
              } catch (e) { /* skip */ }
            }
            if (loaded.length === 0) continue;

            // Appointment heading above its photos
            if (photosY > doc.internal.pageSize.height - 60) {
              doc.addPage();
              photosY = 20;
            }
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...PRIMARY_COLOR);
            doc.text(`${formatDate(appt.date)} — ${appt.provider_name}`, 14, photosY);
            photosY += 6;
            doc.setTextColor(0, 0, 0);

            photosY = appendAttachmentsToPdf(doc, loaded, photosY, `Photos (${loaded.length})`);
            photosY += 4;
          }
        }
      }
    }
  } catch (e) {
    // Non-fatal — skip the photos section if anything fails
  }

  addFooter(doc);
  doc.save(`care-team-summary-${new Date().toISOString().split('T')[0]}.pdf`);
};
